import axios from 'axios';
import { ApiResponse, City, NovaPoshtaRequest, Warehouse } from '../types';
import { getUrlParameter } from '../hooks/useForm';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.novaposhta.ua/v2.0/json/';
const API_KEY = 'f5c162aa591f4fa2fd8c50f88e1ffe46';

// Mock data for development when API key is not available
const MOCK_CITIES: City[] = [
  {
    Ref: 'city1',
    Description: 'Київ',
    Area: 'Київська область',
    SettlementType: 'city',
    IsBranch: '1'
  },
  {
    Ref: 'city2',
    Description: 'Львів',
    Area: 'Львівська область',
    SettlementType: 'city',
    IsBranch: '1'
  },
  {
    Ref: 'city3',
    Description: 'Одеса',
    Area: 'Одеська область',
    SettlementType: 'city',
    IsBranch: '1'
  }
];

const MOCK_WAREHOUSES: Record<string, Warehouse[]> = {
  'city1': [
    {
      Ref: 'wh1',
      Description: 'Відділення №1: вул. Хрещатик, 22',
      ShortAddress: 'вул. Хрещатик, 22',
      Phone: '380441234567',
      TypeOfWarehouse: 'Warehouse',
      Number: '1'
    },
    {
      Ref: 'wh2',
      Description: 'Відділення №2: вул. Велика Васильківська, 100',
      ShortAddress: 'вул. Велика Васильківська, 100',
      Phone: '380441234568',
      TypeOfWarehouse: 'Warehouse',
      Number: '2'
    }
  ],
  'city2': [
    {
      Ref: 'wh3',
      Description: 'Відділення №1: вул. Городоцька, 50',
      ShortAddress: 'вул. Городоцька, 50',
      Phone: '380321234567',
      TypeOfWarehouse: 'Warehouse',
      Number: '1'
    }
  ],
  'city3': [
    {
      Ref: 'wh4',
      Description: 'Відділення №1: вул. Дерибасівська, 10',
      ShortAddress: 'вул. Дерибасівська, 10',
      Phone: '380481234567',
      TypeOfWarehouse: 'Warehouse',
      Number: '1'
    }
  ]
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for logging and error handling
api.interceptors.request.use(
  (config) => {
    console.log('API Request:', config.data);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for logging and error handling
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.data);
    return response;
  },
  (error) => {
    console.error('API Error:', error);
    if (error.response) {
      console.error('Error Response Data:', error.response.data);
      console.error('Error Response Status:', error.response.status);
      console.error('Error Response Headers:', error.response.headers);
    } else if (error.request) {
      console.error('Error Request:', error.request);
    }
    return Promise.reject(error);
  }
);

// Check if we should use mock data (when API key is not available)
const useMockData = false; // Set to false to always use real API

export const searchCities = async (query: string): Promise<City[]> => {
  // Use mock data if enabled
  if (useMockData) {
    console.log('Using mock data for cities');
    return MOCK_CITIES.filter(city =>
      city.Description.toLowerCase().includes(query.toLowerCase())
    );
  }

  try {
    console.log('Searching cities with query:', query);
    const request: NovaPoshtaRequest = {
      apiKey: API_KEY,
      modelName: 'Address',
      calledMethod: 'searchSettlements',
      methodProperties: {
        CityName: query,
        Limit: 20,
      },
    };

    const response = await api.post<ApiResponse<any>>('', request);
    console.log('Raw API response:', JSON.stringify(response.data, null, 2));

    if (response.data.success && response.data.data && response.data.data.length > 0) {
      // Check if the response has the expected structure
      if (response.data.data[0].Addresses && Array.isArray(response.data.data[0].Addresses)) {
        console.log(`Found ${response.data.data[0].Addresses.length} cities in Addresses array`);
        return response.data.data[0].Addresses.map((address: any) => ({
          Ref: address.DeliveryCity || '',
          Description: address.Present || address.MainDescription || address.Description || '',
          Area: address.Area || '',
          SettlementType: address.SettlementType || '',
          IsBranch: '1'
        }));
      }
      // Alternative structure sometimes returned by the API
      else if (Array.isArray(response.data.data)) {
        console.log(`Found ${response.data.data.length} cities in data array`);
        return response.data.data.map((item: any) => ({
          Ref: item.DeliveryCity || '',
          Description: item.Present || item.Description || item.SettlementDescription || '',
          Area: item.Area || item.AreaDescription || '',
          SettlementType: item.SettlementType || '',
          IsBranch: '1'
        }));
      }
    }

    console.log('No cities found or API returned unsuccessful response');
    return [];
  } catch (error) {
    console.error('Error searching cities:', error);
    return [];
  }
};

export const getWarehouses = async (cityRef: string, warehouseNumber?: string): Promise<Warehouse[]> => {
  // Use mock data if enabled
  if (useMockData) {
    console.log('Using mock data for warehouses');
    let warehouses = MOCK_WAREHOUSES[cityRef] || [];

    // Filter by warehouse number if provided
    if (warehouseNumber) {
      console.log(`Filtering mock warehouses by number: ${warehouseNumber}`);
      warehouses = warehouses.filter(wh =>
        wh.Number && wh.Number.toLowerCase().includes(warehouseNumber.toLowerCase())
      );
    }

    return warehouses;
  }

  try {
    console.log('Getting warehouses for city ref:', cityRef);
    const request: NovaPoshtaRequest = {
      apiKey: API_KEY,
      modelName: 'AddressGeneral',
      calledMethod: 'getWarehouses',
      methodProperties: {
        CityRef: cityRef,
        Limit: 100,
        // Add warehouse number to the API request if provided
        ...(warehouseNumber ? { WarehouseId: warehouseNumber } : {})
      },
    };

    const response = await api.post<ApiResponse<Warehouse>>('', request);
    console.log('Raw warehouse response:', JSON.stringify(response.data, null, 2));

    if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
      console.log(`Found ${response.data.data.length} warehouses`);
      const warehouses = response.data.data.map((warehouse: any) => ({
        Ref: warehouse.Ref || '',
        Description: warehouse.Description || '',
        ShortAddress: warehouse.ShortAddress || '',
        Phone: warehouse.Phone || '',
        TypeOfWarehouse: warehouse.TypeOfWarehouse || '',
        Number: warehouse.Number || ''
      }));

      // Additional client-side filtering by warehouse number if needed
      if (warehouseNumber && warehouses.length > 0) {
        console.log(`Additional filtering by warehouse number: ${warehouseNumber}`);
        return warehouses.filter(wh =>
          wh.Number && wh.Number.toLowerCase().includes(warehouseNumber.toLowerCase()) ||
          wh.Description && wh.Description.toLowerCase().includes(warehouseNumber.toLowerCase())
        );
      }

      return warehouses;
    }

    console.log('No warehouses found or API returned unsuccessful response');
    return [];
  } catch (error) {
    console.error('Error getting warehouses:', error);
    return [];
  }
};

export const submitForm = async (formData: any): Promise<boolean> => {
  try {
    console.log('Submitting form data:', formData);
    // Get the submission endpoint from environment variables
    const submissionEndpoint = process.env.REACT_APP_SUBMISSION_ENDPOINT || 'https://api.airtable.com/v0/appXXXXXXXXXXXXXX/Table';

    // Get the Airtable API token from environment variables
    const airtableToken = process.env.REACT_APP_AIRTABLE_TOKEN;

    if (!airtableToken) {
      console.error('Airtable token is missing. Please check your .env file.');
      return false;
    }
    var nameItem = getUrlParameter('item') ?? '¯\_(ツ)_/¯';
    const decodedNameItem = decodeURIComponent(nameItem);
    // Format the data according to Airtable's API requirements
    const airtableData = {
      records: [
        {
          fields: {
            Name: decodedNameItem,
            Price: Number(getUrlParameter('amount')) ?? 0.0,
            Customer: formData.name,
            Phone: formData.phone,
            Warehouse: formData.warehouse ? formData.warehouse.Description : '',
            City: formData.city ? formData.city.Description : ''
          }
        }
      ]
    };

    console.log('Formatted data for Airtable:', airtableData);

    // Make the API request with the Authorization header
    const response = await axios.post(submissionEndpoint, airtableData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${airtableToken}`
      }
    });

    console.log('Form submission response:', response.data);
    return response.status === 200;
  } catch (error) {
    console.error('Error submitting form:', error);
    return false;
  }
};